// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Beeline — presentational HTML builders. No game logic, no network; just markup.

import { escapeHtml } from './dom';
import { compass, formatKm } from './geo';
import { heatColor, PALETTE } from './palette';
import { country } from './data';
import { MODE_IDS, modeOf, type Mode } from './modes';
import type { GameState, Guess } from './game';
import type { PeerResult } from './race';

export const ATTRIB =
  'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · ' +
  '<a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>';

/** One guessed country as a list row: warmth swatch, name, distance, arrow, %. */
export function guessRowHtml(g: Guess): string {
  const col = heatColor(g.pct, g.correct);
  if (g.correct) {
    return `<li class="grow correct" style="--heat:${PALETTE.solved}">
      <span class="g-swatch"></span>
      <span class="g-name">${escapeHtml(g.name)}</span>
      <span class="g-dist">found it!</span>
      <span class="g-bar"><span class="g-fill" style="width:100%"></span></span>
      <span class="g-pct">100%</span>
    </li>`;
  }
  return `<li class="grow" style="--heat:${col}">
    <span class="g-swatch"></span>
    <span class="g-name">${escapeHtml(g.name)}</span>
    <span class="g-dist">${formatKm(g.km)}</span>
    <span class="g-arrow" style="transform:rotate(${g.bearing.toFixed(0)}deg)" title="${compass(g.bearing)}" aria-label="Toward the mystery: ${compass(g.bearing)}">↑</span>
    <span class="g-bar"><span class="g-fill" style="width:${g.pct}%"></span></span>
    <span class="g-pct">${g.pct}%</span>
  </li>`;
}

/**
 * The mystery-country silhouette tile that floats over the map. On a small phone
 * it covers a big slice of the map, so it carries a toggle to collapse it down to
 * a small pill and reveal the map underneath — expand it again when you want a look.
 */
export function silhouetteBoxHtml(): string {
  return `<button class="silbox-toggle" id="silToggle" type="button" aria-controls="silbox" aria-expanded="true" aria-label="Hide the country shape">Hide</button>
    <canvas id="sil" width="120" height="120"></canvas>
    <span class="sil-cap">Find this country</span>`;
}

export interface MenuOpts {
  modeId: string;
  best: number;
  muted: boolean;
  dateStr: string;
}

export function menuHtml(o: MenuOpts): string {
  const chips = MODE_IDS.map(
    (id) =>
      `<button class="chip${id === o.modeId ? ' on' : ''}" data-mode="${id}" role="radio" aria-checked="${id === o.modeId}">${modeOf(id).name}</button>`,
  ).join('');
  return `
    <section class="screen menu">
      <header class="brand">
        <div class="brand-mark" aria-hidden="true"></div>
        <h1 class="brand-title">Beeline</h1>
        <p class="brand-tag">Home in on the hidden country. Every guess tells you how far it is — and which way to go.</p>
      </header>
      <div class="mode-pick" role="radiogroup" aria-label="Mode">${chips}</div>
      <p class="mode-blurb" id="modeBlurb">${escapeHtml(modeOf(o.modeId).blurb)}</p>
      <div class="menu-actions">
        <button class="btn primary" id="playDaily">Today’s country <span class="sub">${escapeHtml(o.dateStr)}</span></button>
        <button class="btn" id="playRandom">Random country <span class="sub">endless streak</span></button>
        <button class="btn" id="playFriends">Play with friends</button>
      </div>
      <div class="menu-foot">
        <button class="linkbtn" id="help">How to play</button>
        <button class="linkbtn" id="about">About</button>
        <button class="linkbtn" id="mute">${o.muted ? 'Sound: off' : 'Sound: on'}</button>
      </div>
      ${o.best > 0 ? `<p class="best">Best streak: <strong>${o.best}</strong></p>` : ''}
    </section>`;
}

export function helpHtml(): string {
  return `
    <div class="sheet-card">
      <h2>How to play</h2>
      <p>Somewhere on Earth, one country is hidden. Type any country to guess it.</p>
      <ul class="howto">
        <li><b>Distance</b> — how far your guess is from the mystery, as the crow flies.</li>
        <li><b>Arrow</b> — points from your guess toward the hidden country.</li>
        <li><b>Warmth</b> — closer countries glow hotter on the map: <span class="key" style="background:${PALETTE.cold}"></span> cold → <span class="key" style="background:${PALETTE.hot}"></span> hot.</li>
      </ul>
      <p>Name the hidden country in as few guesses as you can. In <b>Silhouette</b> mode you also see its shape from the start.</p>
      <p class="fine">Uses a curated list of real countries — obscure micro-states only appear in <b>Territories</b> mode, so a rare answer is a choice, not a surprise.</p>
      <button class="btn primary sheet-close">Got it</button>
    </div>`;
}

export function aboutHtml(): string {
  return `
    <div class="sheet-card">
      <h2>About Beeline</h2>
      <p>A geography deduction game. Find the hidden country using nothing but distance and direction — solo, on a shared daily, or racing friends on the same mystery.</p>
      <p class="fine">Borders and centroids come from <b>Natural Earth</b> (public domain). No map tiles, no photos, no tracking — the whole world is bundled in the page and runs offline. Country facts aren’t copyrightable.</p>
      <p class="fine">Multiplayer is peer-to-peer (WebRTC): a public signalling relay only introduces the players, and no data is stored on any server. Nothing about the answer ever crosses the wire — every device works out the same mystery from a shared seed.</p>
      <p class="fine">${ATTRIB}</p>
      <button class="btn primary sheet-close">Close</button>
    </div>`;
}

/** Solo end-of-round card. */
export function soloResultsHtml(
  state: GameState,
  mode: Mode,
  streak: number,
  best: number,
  daily: boolean,
): string {
  const mystery = country(state.mysteryId)!;
  const solved = state.solved;
  const head = solved
    ? `<h2 class="res-win">Solved in ${state.guesses.length}!</h2>`
    : `<h2 class="res-lose">Out of guesses</h2>`;
  const answer = `<p class="res-answer">The country was <strong style="color:${PALETTE.solved}">${escapeHtml(mystery.name)}</strong>.</p>`;
  const streakLine = daily
    ? `<p class="res-streak">Daily challenge · ${escapeHtml(mode.name)}</p>`
    : solved
      ? `<p class="res-streak">Streak: <strong>${streak}</strong>${best > streak ? ` · best ${best}` : ''}</p>`
      : `<p class="res-streak">Streak ended${best > 0 ? ` · best ${best}` : ''}</p>`;
  return `
    <section class="screen results">
      ${head}
      ${answer}
      ${streakLine}
      <ul class="guesslist small">${state.guesses.map(guessRowHtml).join('')}</ul>
    </section>`;
}

/** Every player's breakdown (principle #9) — the shared moment of comparison. */
export function multiResultsHtml(
  ranked: PeerResult[],
  selfSeat: number | null,
  mysteryId: string,
  tally: number[],
): string {
  const mystery = country(mysteryId)!;
  const solvedCount = ranked.filter((r) => r.solved).length;
  const rows = ranked
    .map((r, i) => {
      const you = r.seat === selfSeat;
      const status = r.left
        ? 'left'
        : !r.over
          ? `guessing… (${r.guessCount})`
          : r.solved
            ? `solved in ${r.guessCount}`
            : `gave up (${r.bestPct}% closest)`;
      const trail = r.trail.length
        ? `<div class="pr-trail">${r.trail
            .map(
              (g) =>
                `<span class="pill" style="--heat:${heatColor(g.pct, g.correct)}" title="${escapeHtml(country(g.id)?.name ?? g.id)}">${escapeHtml(country(g.id)?.name ?? g.id)}</span>`,
            )
            .join('')}</div>`
        : '';
      return `<li class="prow${you ? ' you' : ''}${r.solved ? ' solved' : ''}">
        <div class="pr-head">
          <span class="pr-rank">${r.solved ? i + 1 : '—'}</span>
          <span class="pr-name">${escapeHtml(r.name)}${you ? ' (you)' : ''}</span>
          <span class="pr-wins">${tally[r.seat] ? `★${tally[r.seat]}` : ''}</span>
          <span class="pr-status">${status}</span>
        </div>
        ${trail}
      </li>`;
    })
    .join('');
  return `
    <section class="screen results">
      <h2 class="res-win">${solvedCount ? 'Round over' : 'Nobody found it'}</h2>
      <p class="res-answer">The country was <strong style="color:${PALETTE.solved}">${escapeHtml(mystery.name)}</strong>.</p>
      <ul class="pr-list">${rows}</ul>
    </section>`;
}
